import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { money } from './kit';
import {
  ROLLUP_COLUMNS,
  filterRollup,
  rollupCsv,
  rollupRows,
  rollupTotals,
  sortRollup,
} from './lib/spendRollup';

/**
 * Spend across every campaign (subsystem 3).
 *
 * The project header answers "is this campaign inside its numbers". This
 * answers the same question across the book at once, which is the one a head
 * of business asks and the sheet could never answer without a pivot table.
 *
 * Committed spend is gross and counted from submission, so an overrun shows
 * before it is paid. A campaign with no agreed internal cost shows a dash
 * rather than a variance against zero — the totals say how many of those
 * there are instead, because an incomplete figure is worth knowing about.
 */

export const SPEND_CSS = `
.sp-card { border: 1px solid var(--line); border-radius: var(--radius); background: var(--surface); overflow: hidden; }
.sp-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 14px 16px; border-bottom: 1px solid var(--line); }
.sp-title { font-size: 12px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--ink-4); margin: 0; }
.sp-sub { font-size: 12.5px; color: var(--ink-4); margin: 3px 0 0; }
.sp-tools { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.sp-check { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--ink-3); }
.sp-figs { display: flex; flex-wrap: wrap; gap: 10px; padding: 13px 16px; border-bottom: 1px solid var(--line); }
.sp-fig { flex: 1 1 130px; }
.sp-fig__label { font-size: 10.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-4); margin: 0 0 3px; }
.sp-fig__value { font-size: 16px; font-weight: 800; color: var(--ink); }
.sp-scroll { overflow-x: auto; }
.sp-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 760px; }
.sp-table th, .sp-table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid var(--line); white-space: nowrap; }
.sp-table th { font-size: 10.5px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--ink-4); background: var(--canvas); position: sticky; top: 0; }
.sp-table th button { all: unset; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; }
.sp-table th button:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }
.sp-table td.sp-num, .sp-table th.sp-num { text-align: right; }
.sp-table tbody tr:hover { background: var(--canvas); }
.sp-name { font-weight: 650; color: var(--ink); }
.sp-over { color: #92400E; font-weight: 750; }
/* Shown only when the rollup is built from fewer invoices than match the
   filters. A figure that is quietly partial is worse than no figure. */
.sp-partial { font-size: 12.5px; color: #92400E; background: #FFFBEB; border-bottom: 1px solid #FDE68A; padding: 10px 16px; margin: 0; }
.sp-muted { color: var(--ink-4); }
.sp-foot td { font-weight: 800; color: var(--ink); background: var(--canvas); border-bottom: none; }
.sp-empty { padding: 28px 16px; text-align: center; font-size: 13px; color: var(--ink-4); }
@media (max-width: 600px) { .sp-head { padding: 12px; } .sp-figs { padding: 12px; } }
`;

const NUMERIC = ROLLUP_COLUMNS.filter((column) => column.type !== 'text').map((c) => c.key);

const cellFor = (row, key) => {
  if (row[key] === null || row[key] === undefined) return <span className="sp-muted">—</span>;
  if (key === 'percent') return `${row[key]}%`;
  if (key === 'count') return row[key];
  return money(row[key]);
};

function Figure({ label, value }) {
  return (
    <div className="sp-fig">
      <p className="sp-fig__label">{label}</p>
      <div className="sp-fig__value iv-num">{value}</div>
    </div>
  );
}

export default function SpendAcrossCampaigns({ campaigns, invoices, coverage }) {
  const [sort, setSort] = useState({ key: 'invoiced', direction: 'desc' });
  const [overOnly, setOverOnly] = useState(false);
  const [search, setSearch] = useState('');

  const rows = useMemo(() => rollupRows(campaigns, invoices), [campaigns, invoices]);

  const shown = useMemo(
    () => sortRollup(filterRollup(rows, { overOnly, search }), sort.key, sort.direction),
    [rows, overOnly, search, sort],
  );

  const totals = useMemo(() => rollupTotals(shown), [shown]);

  function sortBy(key) {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'desc' ? 'asc' : 'desc' }
        : { key, direction: NUMERIC.includes(key) ? 'desc' : 'asc' },
    );
  }

  function exportCsv() {
    try {
      const blob = new Blob([rollupCsv(shown)], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `campaign-spend-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not start the download in this browser.');
    }
  }

  const partial = coverage && !coverage.complete;

  return (
    <section className="sp-card" aria-label="Spend across campaigns">
      {partial ? (
        <p className="sp-partial" role="status">
          These figures cover {coverage.loaded} of {coverage.total} matching invoices. Narrow the
          filters to bring the rest into view — the totals below are not the whole campaign.
        </p>
      ) : null}
      <div className="sp-head">
        <div>
          <p className="sp-title">Spend across campaigns</p>
          <p className="sp-sub">
            {totals.campaigns} campaign{totals.campaigns === 1 ? '' : 's'}
            {totals.overCount > 0
              ? ` · ${totals.overCount} over the agreed internal cost`
              : ' · all inside their agreed cost'}
          </p>
        </div>
        <div className="sp-tools">
          <input
            className="iv-input iv-input--sm"
            placeholder="Search campaign or brand"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search campaign or brand"
          />
          <label className="sp-check">
            <input
              type="checkbox"
              checked={overOnly}
              onChange={(event) => setOverOnly(event.target.checked)}
            />
            Over only
          </label>
          <button type="button" className="iv-btn iv-btn--ghost iv-btn--sm" onClick={exportCsv}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="sp-figs">
        <Figure label="Client cost" value={money(totals.client_cost)} />
        <Figure label="Agreed internal" value={money(totals.internal_cost)} />
        <Figure label="Invoiced" value={money(totals.invoiced)} />
        <Figure label="Paid" value={money(totals.paid)} />
        <Figure label="Outstanding" value={money(totals.outstanding)} />
      </div>

      {shown.length === 0 ? (
        <p className="sp-empty">No campaigns match.</p>
      ) : (
        <div className="sp-scroll">
          <table className="sp-table">
            <thead>
              <tr>
                {ROLLUP_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className={column.type === 'text' ? undefined : 'sp-num'}
                    aria-sort={
                      sort.key === column.key
                        ? sort.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button type="button" onClick={() => sortBy(column.key)}>
                      {column.header}
                      {sort.key === column.key ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.campaign_id}>
                  <td className="sp-name">{row.name || `Campaign ${row.campaign_id}`}</td>
                  <td>{row.brand || <span className="sp-muted">—</span>}</td>
                  {ROLLUP_COLUMNS.slice(2).map((column) => (
                    <td
                      key={column.key}
                      className={`sp-num iv-num${
                        column.key === 'variance' && row.over ? ' sp-over' : ''
                      }`}
                    >
                      {cellFor(row, column.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="sp-foot">
                <td>Total</td>
                <td />
                <td className="sp-num iv-num">{money(totals.client_cost)}</td>
                <td className="sp-num iv-num">{money(totals.internal_cost)}</td>
                <td className="sp-num iv-num">{money(totals.invoiced)}</td>
                <td className="sp-num iv-num">{money(totals.paid)}</td>
                <td className="sp-num iv-num">{money(totals.outstanding)}</td>
                <td className="sp-num iv-num">
                  {money(totals.invoiced - totals.internal_cost)}
                </td>
                <td />
                <td />
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
